import {
  call,
  put,
  takeEvery,
  all, select,
  delay,
} from 'redux-saga/effects';
import API from 'library/constants/api';
import request from 'library/helpers/request';
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
} from 'store/shared/actions';
import {
  getBrowseItems,
  updateBrowseItems,
  resetBrowseItemsActions,
  resetBrowseItems, resetBrowseItems as resetBrowseItemsAction, setBrowseItemsFilters,
} from 'store/browse-items/actions';
import {
  getBrowseItems as getBrowseItemsSelector, getBrowseItemsFilters,
  getUpdatedBrowseItems as getUpdatedBrowseItemSelector,
} from 'store/browse-items/selectors';

import {
  getSet as getSetSelector,
} from 'store/set/selectors';

import {
  getProject as getProjectSelector,
} from 'store/project/selectors';
import { updateWishlistItems, resetWishlistItemsActions } from 'store/wishlist-items/actions';
import { getUpdatedWishlistItems } from 'store/wishlist-items/selectors';
import { getSet, resetSet } from 'store/set/actions';
import { setTags } from 'store/tags/actions';
import { getProject, resetProject } from 'store/project/actions';
import * as actions from './actions';
import * as constants from './constants';
import { FILTER_KEYS, TYPE } from '../browse-items/fixtures';

function* getItem(action) {
  try {
    const { data } = yield call(request, `${API.BROWSE_ITEMS}/${action.payload.id}`, 'GET');
    yield put(actions.getItemSuccess(data));
    if(data.tags?.length) {
      yield put(setTags(data.tags));
    }
    const updatedBrowseItems = yield select(getUpdatedBrowseItemSelector({ id: action.payload.id, data }));
    yield put(updateBrowseItems({ data: updatedBrowseItems }));

    const updatedWishlistItems = yield select(getUpdatedWishlistItems({ id: action.payload.id, data }));
    yield put(updateWishlistItems({ data: updatedWishlistItems }));
  } catch (e) {
    yield put(actions.getItemError(e));
    yield put(showErrorNotification({ message: 'Failed to fetch data, please try later.' }));
  }
}

// eslint-disable-next-line import/prefer-default-export
export function* getItemWatcher() {
  yield takeEvery(constants.GET_ITEM, getItem);
}

function* deleteItem(id) {
  // eslint-disable-next-line max-len
  // todo - to be replaced with API.ITEMS after api integration as json web server do not support aggregation
  // so that mock api contains separate table for browse items
  yield call(request, `${API.BROWSE_ITEMS}/${id}`, 'DELETE');
}

function* deleteItems(action) {
  try {
    yield put(showWarningNotification({ message: 'Delete in progress.', isLoading: false }));
    // todo - to be removed after api integration as json web server do not support aggregation
    // so that mock api contains separate table for browse items

    for (let i = 0; i < action.payload.ids.length; i += 1) {
      yield call(deleteItem, action.payload.ids[i]);

      if (i < action.payload.ids.length - 1) {
        yield delay(1000);
      }
    }

    yield put(actions.deleteItemsSuccess());
    yield put(showSuccessNotification({ message: 'Delete success.' }));

    // yield put(setBrowseItemsFilters({}));
    yield put(resetWishlistItemsActions());
    yield put(resetBrowseItemsActions());
    yield put(actions.setUpdatedItems({
      ids: action.payload.ids,
      data: { isDeleted: true },
    }));
    // yield put(getBrowseItems());
  } catch (e) {
    yield put(actions.deleteItemsError(e));
    yield put(showErrorNotification({ message: 'Failed to delete data, please try later.' }));
  }
}

// eslint-disable-next-line import/prefer-default-export
export function* deleteItemsWatcher() {
  yield takeEvery(constants.DELETE_ITEMS, deleteItems);
}

function* batchUpdateItems(action) {
  const {
    validIds = [], invalidIds = [], data, messages,
  } = action.payload;
  try {
    if (invalidIds.length) {
      yield put(showWarningNotification({ message: messages.warning }));
    }

    if (validIds.length) {
      yield call(request, `${API.BROWSE_ITEMS}/bulk/update`, 'PATCH', { data: { ids: validIds, ...data } });
      yield put(actions.setUpdatedItems({
        ids: validIds,
        data,
      }));

      yield all(validIds.map((id) => call(actions.getItem, { id })));

      yield put(showSuccessNotification({ message: messages.success }));
    }

    yield put(actions.batchUpdateItemSuccess());
  } catch (e) {
    yield put(actions.batchUpdateItemError(e));
    yield put(showErrorNotification({ message: messages.error }));
  }
}

// eslint-disable-next-line import/prefer-default-export
export function* batchUpdateItemsWatcher() {
  yield takeEvery(constants.ITEM_BATCH_UPDATE, batchUpdateItems);
}

function* batchUpdateItemsSuccess() {
  const browseItems = yield select(getBrowseItemsSelector);
  const set = yield select(getSetSelector);

  const location = yield select(({ router }) => router?.location);

  if (browseItems.length) {
    // yield put(resetBrowseItems());
    // yield put(getBrowseItems());
    yield put(resetBrowseItemsActions());
  } else if (location?.pathname.includes('/wishlist')) {
    // yield put(resetWishlistItems());
    yield put(resetWishlistItemsActions());
    // yield put(getWishlistItems());
  } else if (set) {
    yield put(getSet({ id: set.id }));
    yield put(resetBrowseItemsActions());
  }
}

export function* batchUpdateItemsSuccessWatcher() {
  yield takeEvery(constants.ITEM_BATCH_UPDATE_SUCCESS, batchUpdateItemsSuccess);
}

function* updateItem(action) {
  const {
    id,
    data: { primaryImage, ...data },
    messages,
  } = action.payload;
  try {
    const requestData = { ...data };

    yield call(request, `${API.BROWSE_ITEMS}/${id}`, 'PATCH', { data: requestData });

    if(primaryImage && primaryImage?.imageOwnsId) {
      yield call(request, `${API.IMAGES}/primary?imageOwnsId=${primaryImage?.imageOwnsId}&id=${id}`, 'PATCH');
    }
    yield put(showSuccessNotification({ message: messages.success }));

    const location = yield select(({ router }) => router?.location);
    yield put(actions.setUpdatedItems({
      ids: [id],
      data: {
        ...Object.keys(data).reduce((result, field) => ({ ...result, [field]: true }), {}),
      },
    }));
    if (location?.pathname.includes('/wishlist')) {
      // yield put(resetWishlistItems());
      yield put(actions.getItem({ id }));
      yield put(resetWishlistItemsActions());
      // yield put(getWishlistItems());
    }
    //
    if (location?.pathname.includes('/browse') && !location?.pathname.includes('/set-details')) {
      yield put(actions.getItem({ id }));
      yield put(resetBrowseItemsActions());
      // yield put(resetBrowseItems());
      // yield put(getBrowseItems());
    }

    if (location?.pathname.includes('/project-details')) {
      const project = yield select(getProjectSelector);

      if (project?.id) {
        yield put(resetProject());
        yield put(getProject({ id: project?.id }));
      }
    }

    if (location?.pathname.includes('/set-details')) {
      yield put(resetBrowseItemsActions());
      yield put(actions.resetItem());
      yield put(actions.getItem({ id: action.payload.id }));

      const set = yield select(getSetSelector);

      if (set?.id) {
        yield put(resetSet({ id: set?.id }));
        yield put(getSet({ id: set?.id }));
      }
    }
  } catch (e) {
    // yield put(actions.batchUpdateItemError(e));
    yield put(showErrorNotification({ message: messages.error }));
  }
}

// eslint-disable-next-line import/prefer-default-export
export function* updateItemWatcher() {
  yield takeEvery(constants.ITEM_UPDATE, updateItem);
}

function* createItem(action) {
  const {
    data,
  } = action.payload;
  try {
    yield call(
      request,
      API.BROWSE_ITEMS,
      'POST',
      {
        data,
      },
    );
    yield put(showSuccessNotification({ message: 'New item successfully created' }));

    const location = yield select(({ router }) => router?.location);

    if (!location?.pathname.includes('/set-details')) {
      // save filters
      const filters = yield select(getBrowseItemsFilters);

      yield put(resetBrowseItemsActions());
      yield put(resetBrowseItems());
      yield put(resetBrowseItemsAction());
      // use previously saved filters for proper content refresh
      yield put(setBrowseItemsFilters(filters));
      yield put(getBrowseItems());
    }

    if (location?.pathname.includes('/set-details')) {
      const set = yield select(getSetSelector);

      if(set?.id) {
        yield put(resetSet());
        yield put(getSet({ id: set?.id }));
      }
    }

    yield put(actions.createItemSuccess());
  } catch (e) {
    yield put(actions.createItemError(e));
    yield put(showErrorNotification({ message: 'Oops :(' }));
  }
}

// eslint-disable-next-line import/prefer-default-export
export function* createItemWatcher() {
  yield takeEvery(constants.ITEM_CREATE, createItem);
}

function* likeItem(action) {
  try {
    yield call(request, `${API.BROWSE_ITEMS}/${action.payload.id}/like`, 'PATCH');
    yield put(actions.likeItemSuccess({ id: action.payload.id }));
    yield put(showSuccessNotification({ message: 'Item liked' }));
    yield put(actions.setUpdatedItems({
      ids: [action.payload.id],
      data: {
        like_level: true,
      },
    }));
    yield put(actions.getItem({ id: action.payload.id }));
  } catch (e) {
    yield put(actions.likeItemError(e));
    yield put(showErrorNotification({ message: 'Failed to like item, please try later.' }));
  }
}

// eslint-disable-next-line import/prefer-default-export
export function* likeItemWatcher() {
  yield takeEvery(constants.ITEM_LIKE, likeItem);
}
